import clsx from 'clsx';
import {MRT_ColumnDef, MRT_Row} from 'material-react-table';
import React, {useCallback, useMemo} from 'react';
import {Body, Icon, Tooltip} from '@lightricks/react-design-system';
import {getApprovalBreakdown} from '@/utils/approvalStatusUtils';
import {formatDueDate, formattedDate} from '@/utils/formattedDate';
import getLatestVersionUploadDate from '@/utils/getLatestVersionUploadDate';
import translate from '@/utils/translate';
import {PHASE, Phase} from '@/config/contentSubmissionPhases';
import {MONTH_SHORT_DAY_YEAR_FORMAT} from '@/config/timeFormats';
import {ContentSubmission} from '@/types/contentSubmission';
import {ContentSubmissionAction} from '@/components/content-submission-card';
import getContentTypeLabel from '@/components/content-submission-card/getContentTypeLabel';
import ContentSubmissionTag, {
  mapStateToTagVariant,
} from '@/components/content-submission-tag';
import ContentSubmissionRowMenu from '../../components/content-submission-row-menu/ContentSubmissionRowMenu';
import styles from './Column.module.scss';

export const ENABLE_SORTING = true;

// Shared config for sortable columns so the table header toggles sorting the
// same way the grid's SortByDropdown does: descending first, flipping between
// desc/asc without a "no sort" state. This keeps sorting identical across the
// table and card views (both write the same sortBy/sortDir).
const SORTABLE_COLUMN_PROPS = {
  enableSorting: ENABLE_SORTING,
  sortDescFirst: true,
  enableSortingRemoval: false,
} as const;

const COLUMN_PADDING_PX = 16;
const DATA_COLUMN_SIZE_PX = 179;
const ACTIONS_COLUMN_SIZE_PX = 24;

const headCellProps = {
  sx: {
    paddingLeft: `${COLUMN_PADDING_PX}px`,
    paddingRight: `${COLUMN_PADDING_PX}px`,
  },
};

const cellPaddingProps = {
  sx: {
    paddingLeft: `${COLUMN_PADDING_PX}px`,
    paddingRight: `${COLUMN_PADDING_PX}px`,
    verticalAlign: 'middle',
  },
};

const noPaddingProps = {
  sx: {paddingLeft: 0, paddingRight: 0, verticalAlign: 'middle'},
};

export type UseContentSubmissionsTableColumnsProps = {
  campaignId: string;
  campaignNetwork?: string | null;
  campaignState?: string | null;
  selectedPhase?: Phase;
  isAdmin?: boolean;
  isOffPlatform?: boolean;
  onAction: (
    action: ContentSubmissionAction,
    contentSubmissionId: string,
    contentSubmissionVersionId?: string
  ) => void;
  onRowMenuOpen?: (context: {creator_id?: string; flow_id: string}) => void;
  onRowActionSelected?: (
    action: ContentSubmissionAction,
    context: {creator_id?: string; flow_id: string}
  ) => void;
};

function useContentSubmissionsTableColumns({
  campaignId,
  campaignNetwork,
  campaignState,
  selectedPhase,
  isAdmin,
  isOffPlatform,
  onAction,
  onRowMenuOpen,
  onRowActionSelected,
}: UseContentSubmissionsTableColumnsProps): MRT_ColumnDef<ContentSubmission>[] {
  const renderActionsCell = useCallback(
    ({row}: {row: MRT_Row<ContentSubmission>}) => (
      <ContentSubmissionRowMenu
        contentSubmission={row.original}
        campaignId={campaignId}
        campaignState={campaignState}
        isAdmin={isAdmin}
        isOffPlatform={isOffPlatform}
        onAction={onAction}
        onMenuOpen={onRowMenuOpen}
        onActionSelected={onRowActionSelected}
      />
    ),
    [
      campaignId,
      campaignState,
      isAdmin,
      isOffPlatform,
      onAction,
      onRowMenuOpen,
      onRowActionSelected,
    ]
  );

  const renderAssetCell = useCallback(
    ({row}: {row: MRT_Row<ContentSubmission>}) => {
      const {thumbnailUrls, profile, latestVersionCommentCount} = row.original;
      const thumbnailUrl = thumbnailUrls?.[0];
      const creatorName = profile?.displayName ?? '';
      const hasComments = latestVersionCommentCount > 0;

      return (
        <div className={styles.assetCell}>
          <div className={styles.thumbnailWrapper}>
            {thumbnailUrl ? (
              <img src={thumbnailUrl} alt="" className={styles.thumbnail} />
            ) : (
              <div className={styles.thumbnailPlaceholder} />
            )}
            {hasComments && (
              <div
                className={clsx(
                  styles.commentBubble,
                  latestVersionCommentCount >= 10 &&
                    styles.commentBubbleMultiDigit
                )}
              >
                {latestVersionCommentCount}
              </div>
            )}
          </div>
          <Body size="md" className={styles.creatorName}>
            {creatorName}
          </Body>
        </div>
      );
    },
    []
  );

  const renderContentTypeCell = useCallback(
    ({row}: {row: MRT_Row<ContentSubmission>}) => {
      const {deliverableKind, kind} = row.original;
      return (
        <Body size="md">
          {getContentTypeLabel(deliverableKind, kind, campaignNetwork)}
        </Body>
      );
    },
    [campaignNetwork]
  );

  const renderVersionCell = useCallback(
    ({row}: {row: MRT_Row<ContentSubmission>}) => (
      <Body size="md">
        {row.original.kind === 'mixed_media' ? '-' : row.original.versionCount}
      </Body>
    ),
    []
  );

  const renderStatusCell = useCallback(
    ({row}: {row: MRT_Row<ContentSubmission>}) => {
      const {aasmState, approvalStatus} = row.original;
      const {pendingDepartments, approvedStepsNames} =
        getApprovalBreakdown(approvalStatus);
      const variant = mapStateToTagVariant(
        aasmState as Parameters<typeof mapStateToTagVariant>[0],
        pendingDepartments,
        approvedStepsNames.length,
        row.original.versionCount
      );
      return (
        <ContentSubmissionTag
          variant={variant}
          departments={pendingDepartments}
        />
      );
    },
    []
  );

  const renderOverdueCell = useCallback(
    ({row}: {row: MRT_Row<ContentSubmission>}) =>
      row.original.dueSoon ? (
        <Tooltip
          title={formatDueDate(row.original.dueAt)}
          placement="top"
          type="dark"
          arrow
        >
          <div className={styles.overdueCell}>
            <Icon name="Actions-Alert-Line" size="medium" appearance="danger" />
          </div>
        </Tooltip>
      ) : null,
    []
  );

  const renderUploadDateCell = useCallback(
    ({row}: {row: MRT_Row<ContentSubmission>}) => {
      const uploadDate = getLatestVersionUploadDate(row.original);
      return (
        <Body size="md">
          {uploadDate
            ? formattedDate(new Date(uploadDate), MONTH_SHORT_DAY_YEAR_FORMAT)
            : '-'}
        </Body>
      );
    },
    []
  );

  return useMemo(
    () =>
      [
        {
          id: 'actions',
          header: '',
          enableSorting: false,
          size: ACTIONS_COLUMN_SIZE_PX,
          minSize: ACTIONS_COLUMN_SIZE_PX,
          maxSize: ACTIONS_COLUMN_SIZE_PX,
          muiTableHeadCellProps: headCellProps,
          muiTableBodyCellProps: noPaddingProps,
          Cell: renderActionsCell,
        } as MRT_ColumnDef<ContentSubmission>,
        {
          id: 'asset',
          header: translate('Asset'),
          accessorKey: 'thumbnailUrls',
          enableSorting: false,
          size: DATA_COLUMN_SIZE_PX,
          minSize: DATA_COLUMN_SIZE_PX,
          muiTableHeadCellProps: headCellProps,
          muiTableBodyCellProps: cellPaddingProps,
          Cell: renderAssetCell,
        },
        {
          id: 'kind',
          header: translate('Content Type'),
          accessorKey: 'deliverableKind',
          ...SORTABLE_COLUMN_PROPS,
          size: DATA_COLUMN_SIZE_PX,
          minSize: DATA_COLUMN_SIZE_PX,
          muiTableHeadCellProps: headCellProps,
          muiTableBodyCellProps: cellPaddingProps,
          Cell: renderContentTypeCell,
        },
        {
          id: 'versions_count',
          header: translate('Version'),
          accessorKey: 'versionCount',
          ...SORTABLE_COLUMN_PROPS,
          size: DATA_COLUMN_SIZE_PX,
          minSize: DATA_COLUMN_SIZE_PX,
          muiTableHeadCellProps: headCellProps,
          muiTableBodyCellProps: cellPaddingProps,
          Cell: renderVersionCell,
        },
        {
          id: 'upload_date',
          header: translate('Uploaded'),
          accessorFn: getLatestVersionUploadDate,
          ...SORTABLE_COLUMN_PROPS,
          size: DATA_COLUMN_SIZE_PX,
          minSize: DATA_COLUMN_SIZE_PX,
          muiTableHeadCellProps: headCellProps,
          muiTableBodyCellProps: cellPaddingProps,
          Cell: renderUploadDateCell,
        },
        {
          id: 'aasm_state',
          header: translate('Status'),
          accessorKey: 'aasmState',
          ...SORTABLE_COLUMN_PROPS,
          size: DATA_COLUMN_SIZE_PX,
          minSize: DATA_COLUMN_SIZE_PX,
          muiTableHeadCellProps: headCellProps,
          muiTableBodyCellProps: cellPaddingProps,
          Cell: renderStatusCell,
        },
        {
          id: 'overdue',
          header: translate('Overdue'),
          accessorKey: 'dueSoon',
          ...SORTABLE_COLUMN_PROPS,
          size: DATA_COLUMN_SIZE_PX,
          minSize: DATA_COLUMN_SIZE_PX,
          muiTableHeadCellProps: headCellProps,
          muiTableBodyCellProps: cellPaddingProps,
          Cell: renderOverdueCell,
        },
      ].filter(
        (col) => !(col.id === 'overdue' && selectedPhase === PHASE.ACCEPTED)
      ),
    [
      selectedPhase,
      renderActionsCell,
      renderAssetCell,
      renderContentTypeCell,
      renderVersionCell,
      renderStatusCell,
      renderOverdueCell,
      renderUploadDateCell,
    ]
  );
}

export default useContentSubmissionsTableColumns;
